import React from 'react';
import { Check, Close } from '@mui/icons-material';

interface StatsigGateRowProps {
  name: string;
  description?: string | null;
  isEnabled: boolean;
  evaluatedResult: boolean;
  /** 'pass' | 'fail' | null | undefined */
  overrideStatus?: string | null;
  /** Trailing controls: an actions menu (interactive) or a link (read-only). */
  actions?: React.ReactNode;
}

function rowColor(overrideStatus?: string | null): string {
  if (overrideStatus === 'pass') return 'bg-green-50 hover:bg-green-100';
  if (overrideStatus === 'fail') return 'bg-red-50 hover:bg-red-100';
  return 'bg-gray-50 hover:bg-gray-100';
}

function OverrideBadge({ overrideStatus }: { overrideStatus?: string | null }) {
  if (overrideStatus === 'pass') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        <Check sx={{ fontSize: 14 }} />
        FORCED PASS
      </span>
    );
  }
  if (overrideStatus === 'fail') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
        <Close sx={{ fontSize: 14 }} />
        FORCED FAIL
      </span>
    );
  }
  return null;
}

/**
 * Presentational row shared by the user-scoped (interactive), org-scoped
 * (read-only on the provider page), and org (interactive) gate lists. Trailing
 * controls are passed in via `actions` so the same visual row serves all three.
 */
export function StatsigGateRow({
  name,
  description,
  isEnabled,
  evaluatedResult,
  overrideStatus,
  actions,
}: StatsigGateRowProps) {
  return (
    <div
      className={`flex items-center justify-between px-3 py-2.5 rounded-md transition-colors ${rowColor(
        overrideStatus,
      )}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`text-sm font-mono truncate ${
              overrideStatus ? 'font-medium' : 'text-gray-600'
            }`}
          >
            {name}
          </span>
          {!isEnabled && (
            <span className="text-xs text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">
              disabled
            </span>
          )}
        </div>
        {description && (
          <div className="text-xs text-gray-500 truncate mt-0.5">{description}</div>
        )}
      </div>

      <div className="flex items-center gap-3 ml-4">
        <span
          className={`text-xs px-2 py-0.5 rounded ${
            evaluatedResult ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-600'
          }`}
        >
          {evaluatedResult ? 'PASS' : 'FAIL'}
        </span>

        <OverrideBadge overrideStatus={overrideStatus} />

        {actions}
      </div>
    </div>
  );
}
